import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Trophy, Mail, Lock, User, Phone, Eye, EyeOff } from 'lucide-react';

const API_URL = (typeof window !== "undefined" && window.location ? window.location.origin : process.env.REACT_APP_BACKEND_URL);

const Register = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    role: 'parent'
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [businessLogo, setBusinessLogo] = useState(null);
  const [logoLoaded, setLogoLoaded] = useState(false);
  const [contactInfo, setContactInfo] = useState(null);
  const { register } = useAuth();
  const navigate = useNavigate();

  // Fetch business logo and contact info
  useEffect(() => {
    const fetchBusinessInfo = async () => {
      try {
        const [logoRes, contactRes] = await Promise.all([
          fetch(`${API_URL}/api/admin/logo`),
          fetch(`${API_URL}/api/contact-info`)
        ]);
        
        if (logoRes.ok) {
          const logoData = await logoRes.json();
          if (logoData.logo_url) {
            setBusinessLogo(logoData.logo_url);
          }
        }
        
        if (contactRes.ok) {
          const contactData = await contactRes.json();
          setContactInfo(contactData);
        }
      } catch (error) {
        console.error('Error fetching business info:', error);
      } finally {
        setLogoLoaded(true);
      }
    };
    fetchBusinessInfo();
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      const user = await register({
        name: formData.name,
        email: formData.email,
        password: formData.password,
        phone: formData.phone || null,
        role: formData.role
      });
      
      toast.success(`Welcome to ${contactInfo?.business_name || 'the Academy'}, ${user.name}!`);
      
      if (user.role === 'admin') navigate('/admin');
      else if (user.role === 'coach') navigate('/coach');
      else if (user.role === 'staff') navigate('/staff');
      else if (user.role === 'customer') navigate('/book');
      else navigate('/parent');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 cricket-pattern flex items-center justify-center p-4 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-red-500/5 rounded-full blur-3xl" />
      
      <div className="w-full max-w-md animate-slideUp relative z-10">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center mb-4">
            {logoLoaded && businessLogo ? (
              <img 
                src={`${API_URL}${businessLogo}`} 
                alt={contactInfo?.business_name || "Cricket Academy"} 
                className="h-24 w-auto" 
              />
            ) : !logoLoaded ? (
              <div className="h-24 w-24 rounded-full bg-slate-700/50 animate-pulse" />
            ) : (
              <Trophy className="h-16 w-16 text-blue-400" />
            )}
          </div>
          <p className="text-slate-400 mt-2">Join Elite Cricket Training</p>
          {contactInfo?.address && (
            <p className="text-slate-500 text-sm mt-1">{contactInfo.address}</p>
          )}
        </div>

        <Card className="bg-white/95 backdrop-blur shadow-2xl border-0">
          <CardHeader className="space-y-1 pb-4">
            <CardTitle className="font-heading text-2xl text-center">Create Account</CardTitle>
            <CardDescription className="text-center">
              Register to get started with cricket training
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-slate-700">Full Name</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    placeholder="John Smith"
                    value={formData.name}
                    onChange={handleChange}
                    className="pl-10 h-11 border-slate-200 focus:border-slate-900 focus:ring-slate-900"
                    required
                    data-testid="register-name"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="text-slate-700">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="you@example.com"
                    value={formData.email}
                    onChange={handleChange}
                    className="pl-10 h-11 border-slate-200 focus:border-slate-900 focus:ring-slate-900"
                    required
                    data-testid="register-email"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone" className="text-slate-700">Phone (Optional)</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    placeholder="+1 (555) 000-0000"
                    value={formData.phone}
                    onChange={handleChange}
                    className="pl-10 h-11 border-slate-200 focus:border-slate-900 focus:ring-slate-900"
                    data-testid="register-phone"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="role" className="text-slate-700">I am a</Label>
                <Select 
                  value={formData.role} 
                  onValueChange={(value) => setFormData({ ...formData, role: value })}
                >
                  <SelectTrigger className="h-11 border-slate-200" data-testid="register-role">
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent position="popper" sideOffset={4}>
                    <SelectItem value="parent">Parent (Enroll child)</SelectItem>
                    <SelectItem value="customer">Customer (Lane booking only)</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-slate-700">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={handleChange}
                    className="pl-10 pr-10 h-11 border-slate-200 focus:border-slate-900 focus:ring-slate-900"
                    required
                    data-testid="register-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-slate-700">Confirm Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="confirmPassword"
                    name="confirmPassword"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    className="pl-10 h-11 border-slate-200 focus:border-slate-900 focus:ring-slate-900"
                    required
                    data-testid="register-confirm-password"
                  />
                </div>
              </div>

              <Button
                type="submit"
                className="w-full h-11 bg-slate-900 hover:bg-slate-800 text-white font-semibold tracking-wide uppercase mt-2"
                disabled={loading}
                data-testid="register-submit"
              >
                {loading ? (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Creating Account...
                  </div>
                ) : (
                  'Create Account'
                )}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-slate-600">
                Already have an account?{' '}
                <Link 
                  to="/login" 
                  className="text-blue-600 hover:text-blue-700 font-semibold"
                  data-testid="login-link"
                >
                  Sign In
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Register;
